import { useEffect, useRef, useState } from "react";
import { fetchThreadsByOrder, sendThreadReply, type ThreadListItem } from "@/features/messaggi/api";
import { prettifyApiError } from "@/shared/api";
import { ErrorBanner } from "@/shared/ui/ErrorBanner";
import type { ReturnRecord } from "../api";

/** Testo proposto, sempre modificabile prima dell'invio.
 *
 *  «sarà affidata», mai «spedita»: generare la LDV non prova che il pacco sia
 *  partito (spesso è ancora dal fornitore). Niente link: il codice si cerca su
 *  poste.it e vale su ogni canale, mentre i link esterni i marketplace li
 *  filtrano. SDA consegna solo in Italia; all'estero la sostituzione parte con
 *  BRT, che si segue su brt.it. */
function testoProposto(reference: string, codici: string[], carrier?: string): string {
  const codice = `${codici.length > 1 ? "i codici" : "il codice"} ${codici.join(", ")}`;
  const brt = (carrier ?? "").toUpperCase() === "BRT";
  return (
    `Buongiorno, la sostituzione del suo ordine ${reference} è pronta e sarà affidata al corriere ${brt ? "BRT" : "SDA"}. ` +
    `Potrà seguire la spedizione su ${brt ? "brt.it" : "poste.it"} con ${codice}. ` +
    "Restiamo a disposizione. Cordiali saluti."
  );
}

interface AvvisaClienteTrackingProps {
  /** Pratica la cui etichetta è appena nata. */
  record: ReturnRecord;
  /** Messaggio partito: chi ha la chat a schermo la ricarica. */
  onInviato?: () => void;
}

/**
 * «Avvisare il cliente del tracking?» dopo l'etichetta di una SOSTITUZIONE, se
 * l'ordine ha una chat nei Messaggi. Stessa regola del tasto «Conversazione»
 * della pagina ordine (riferimento ordine esatto); se le chat sono più d'una
 * vale la più recente, anche se chiusa. I canali spenti nei Messaggi (Amazon,
 * WhatsApp) non tornano dalla ricerca, quindi lì la domanda non compare.
 *
 * L'invio va al solo cliente: su Mirakl l'operatore del marketplace resta fuori.
 */
export function AvvisaClienteTracking({ record, onInviato }: AvvisaClienteTrackingProps) {
  const pertinente = record.kind === "sostituzione" && record.tracking_numbers.length > 0;
  const [chat, setChat] = useState<ThreadListItem | null>(null);
  const [testo, setTesto] = useState(() =>
    testoProposto(record.order_reference, record.tracking_numbers, record.carrier),
  );
  const [esito, setEsito] = useState<"da_decidere" | "inviato" | "scartato">("da_decidere");
  const [invio, setInvio] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  // Guardia SINCRONA: due clic nello stesso tick leggono entrambi ``invio`` a
  // false, e al cliente arriverebbero due messaggi identici.
  const inVolo = useRef(false);

  useEffect(() => {
    if (!pertinente) return;
    let vivo = true;
    fetchThreadsByOrder(record.order_reference)
      .then((res) => {
        // Il backend ordina per ultimo messaggio: la prima è la chat più viva.
        if (vivo) setChat(res.items[0] ?? null);
      })
      .catch(() => {
        // Messaggi irraggiungibili o permesso mancante: nessuna domanda. Un
        // errore qui coprirebbe la conferma dell'etichetta, che è andata bene.
      });
    return () => {
      vivo = false;
    };
  }, [pertinente, record.order_reference]);

  if (!chat || esito === "scartato") return null;

  if (esito === "inviato") {
    return (
      <p role="status" className="text-[12.5px] font-medium text-ink">
        Tracking inviato su {chat.source_label} ✓
      </p>
    );
  }

  async function invia(destinazione: ThreadListItem): Promise<void> {
    if (inVolo.current) return;
    inVolo.current = true;
    setInvio(true);
    setErrore(null);
    try {
      await sendThreadReply(destinazione.id, testo.trim(), [], "customer");
      setEsito("inviato");
      onInviato?.();
    } catch (e) {
      setErrore(prettifyApiError(e));
    } finally {
      inVolo.current = false;
      setInvio(false);
    }
  }

  return (
    <section aria-label="Avvisa il cliente" className="rounded-md border border-line p-3">
      <p className="text-[12.5px] font-medium text-ink">Avvisare il cliente del tracking?</p>
      <p className="mb-2 text-[11px] text-ink-2">
        C'è una chat {chat.source_label} per questo ordine: il messaggio parte lì.
      </p>
      <textarea
        aria-label="Messaggio al cliente"
        value={testo}
        rows={4}
        disabled={invio}
        onChange={(e) => setTesto(e.target.value)}
        className="w-full resize-y rounded-sm border border-line px-2 py-1.5 text-[12.5px] leading-relaxed text-ink focus:border-line-2 focus:outline-none focus-visible:ring-1 focus-visible:ring-accent/40 disabled:opacity-50"
      />
      <ErrorBanner message={errore} className="mt-2" />
      <div className="mt-2 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={() => setEsito("scartato")}
          disabled={invio}
          className="rounded-sm border border-line px-[12px] py-[6px] text-[12px] font-medium text-ink-2 hover:bg-paper disabled:opacity-50"
        >
          Non inviare
        </button>
        <button
          type="button"
          onClick={() => void invia(chat)}
          disabled={invio || !testo.trim()}
          aria-busy={invio}
          className="rounded-sm border border-accent bg-accent-soft px-[12px] py-[6px] text-[12px] font-medium text-ink hover:bg-accent-2 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
        >
          {invio ? "Invio…" : "Invia al cliente"}
        </button>
      </div>
    </section>
  );
}
